<!-- <ReusableTable
    :data="orders"
    :columns="tableHeaders"
    :show-edit="false"
    :show-delete="false"
    :show-search="false"
    :show-pagination="true"
    :page-size="10"
    @edit="handlePageChange"
    @delete="handlePageChange"
    @page-change="handlePageChange"
    @sort-change="handlePageChange"
    @search="handlePageChange"
></ReusableTable> -->
<template>
  <div class="datatable">
    <!-- Search Filter -->
    <div class="datatable-header" v-if="showSearch">
      <div class="search-box">
        <input
          type="text"
          v-model.trim="searchTerm"
          :placeholder="searchPlaceholder"
          class="search-input"
          @input="onSearch"
        />
        <span class="search-icon">🔍</span>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container">
      <table class="datatable-table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[
                {
                  sortable: column.sortable,
                  'sort-asc': sortBy === column.key && sortDirection === 'asc',
                  'sort-desc':
                    sortBy === column.key && sortDirection === 'desc',
                },
                column.align ? `text-${column.align}` : '',
              ]"
              @click="column.sortable ? sortTable(column.key) : null"
            >
              <div
                class="header-content"
                :class="[
                  column.align === 'center'
                    ? 'justify-center'
                    : column.align === 'right'
                    ? 'justify-end'
                    : 'justify-start',
                ]"
              >
                <span>{{ column.title }}</span>
                <span v-if="column.sortable" class="sort-indicator">
                  {{
                    sortBy === column.key
                      ? sortDirection === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"
                  }}
                </span>
              </div>
            </th>
            <th v-if="hasActions" class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="paginatedData.length === 0">
            <td
              :colspan="columns.length + (hasActions ? 1 : 0)"
              class="no-data"
            >
              {{ noDataText }}
            </td>
          </tr>
          <tr
            v-else
            v-for="(row, index) in paginatedData"
            :key="getRowKey(row, index)"
            :class="[
              rowClass ? rowClass(row) : '',
              'cursor-pointer hover:bg-gray-50 transition-colors',
            ]"
            @click="$emit('row-click', row)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :class="[column.align ? `text-${column.align}` : '']"
            >
              <slot
                :name="`column-${column.key}`"
                :value="row[column.key]"
                :row="row"
                :index="index"
              >
                {{ formatValue(row[column.key], column) }}
              </slot>
            </td>
            <td v-if="hasActions" class="actions-cell" @click.stop>
              <slot name="actions" :row="row" :index="index">
                <!-- Default actions slot content -->
                <button
                  v-if="showEdit"
                  @click.stop="$emit('edit', row)"
                  class="btn btn-edit"
                >
                  Edit
                </button>
                <button
                  v-if="showDelete"
                  @click.stop="$emit('delete', row)"
                  class="btn btn-delete"
                >
                  Delete
                </button>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="datatable-footer" v-if="showPagination">
      <div class="pagination-info">
        Showing {{ startIndex }} to {{ endIndex }} of
        {{ filteredData.length }} entries
      </div>
      <div class="pagination-controls">
        <button
          @click="previousPage"
          :disabled="currentPage === 1"
          class="pagination-btn"
        >
          Previous
        </button>

        <span
          v-for="page in visiblePages"
          :key="page"
          @click="goToPage(page)"
          :class="{
            'page-number': true,
            active: page === currentPage,
            ellipsis: page === '...',
          }"
        >
          {{ page }}
        </span>

        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="pagination-btn"
        >
          Next
        </button>

        <select
          v-model="internalPageSize"
          @change="onPageSizeChange"
          class="page-size-select"
        >
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";

export default {
  name: "DataTable",
  emits: [
    "edit",
    "delete",
    "page-change",
    "sort-change",
    "search",
    "row-click",
  ],
  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    columns: {
      type: Array,
      required: true,
      default: () => [],
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    showPagination: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: "Search...",
    },
    noDataText: {
      type: String,
      default: "Pas de commandes trouvées",
    },
    showEdit: {
      type: Boolean,
      default: false,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
    rowKey: {
      type: String,
      default: "id",
    },
    /** Fonction optionnelle (row) => string : classes CSS à appliquer à chaque <tr> (ex. bordure couleur selon statut) */
    rowClass: {
      type: Function,
      default: null,
    },
  },
  setup(props, { emit }) {
    // Reactive data - use different name for internal state
    const currentPage = ref(1);
    const sortBy = ref("");
    const sortDirection = ref("asc");
    const searchTerm = ref("");
    const internalPageSize = ref(props.pageSize); // Changed from pageSize to internalPageSize

    // Computed properties
    const hasActions = computed(() => props.showEdit || props.showDelete);

    const filteredData = computed(() => {
      let filtered = [...props.data];

      // Apply search filter
      if (searchTerm.value) {
        const term = searchTerm.value.toLowerCase();
        filtered = filtered.filter((row) =>
          props.columns.some((column) =>
            String(row[column.key]).toLowerCase().includes(term),
          ),
        );
      }

      // Apply sorting
      if (sortBy.value) {
        filtered.sort((a, b) => {
          let aValue = a[sortBy.value];
          let bValue = b[sortBy.value];
          
          const dateRegex = /^\d{2}\/\d{2}\/\d{4}/;
          if (typeof aValue === "string" && dateRegex.test(aValue)) {
      
            const parseDate = (str) => {
              const [datePart, timePart] = str.split(" ");
              const [day, month, year] = datePart.split("/");
              const [hour, minute] = (timePart || "00:00").split(":");
              return new Date(+year, +month - 1, +day, +hour, +minute).getTime();
            };
            aValue = parseDate(aValue);
            bValue = parseDate(bValue);
          } else if (typeof aValue === "string") {
            // Handle other string types
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }

          if (aValue < bValue) return sortDirection.value === "asc" ? -1 : 1;
          if (aValue > bValue) return sortDirection.value === "asc" ? 1 : -1;
          return 0;
        });
      }

      return filtered;
    });

    const totalPages = computed(() =>
      Math.ceil(filteredData.value.length / internalPageSize.value),
    );

    const paginatedData = computed(() => {
      // If pagination is disabled, return all filtered data
      if (!props.showPagination) {
        return filteredData.value;
      }

      const start = (currentPage.value - 1) * internalPageSize.value;
      const end = start + internalPageSize.value;
      return filteredData.value.slice(start, end);
    });

    const startIndex = computed(() =>
      filteredData.value.length > 0
        ? (currentPage.value - 1) * internalPageSize.value + 1
        : 0,
    );

    const endIndex = computed(() =>
      Math.min(
        currentPage.value * internalPageSize.value,
        filteredData.value.length,
      ),
    );

    const visiblePages = computed(() => {
      const pages = [];
      const total = totalPages.value;
      const current = currentPage.value;
      const delta = 2;

      for (let i = 1; i <= total; i++) {
        if (
          i === 1 ||
          i === total ||
          (i >= current - delta && i <= current + delta)
        ) {
          pages.push(i);
        } else if (pages[pages.length - 1] !== "...") {
          pages.push("...");
        }
      }

      return pages;
    });

    // Methods
    const sortTable = (columnKey) => {
      if (sortBy.value === columnKey) {
        sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
      } else {
        sortBy.value = columnKey;
        sortDirection.value = "asc";
      }
      emit("sort-change", {
        column: columnKey,
        direction: sortDirection.value,
      });
    };

    const goToPage = (page) => {
      if (page === "..." || page === currentPage.value) return;
      currentPage.value = page;
      emit("page-change", {
        currentPage: page,
        pageSize: internalPageSize.value,
      });
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
        emit("page-change", {
          currentPage: currentPage.value,
          pageSize: internalPageSize.value,
        });
      }
    };

    const previousPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        emit("page-change", {
          currentPage: currentPage.value,
          pageSize: internalPageSize.value,
        });
      }
    };

    const onPageSizeChange = () => {
      currentPage.value = 1;
      emit("page-change", {
        currentPage: currentPage.value,
        pageSize: internalPageSize.value,
      });
    };

    const onSearch = () => {
      currentPage.value = 1;
      emit("search", searchTerm.value);
    };

    const getRowKey = (row, index) => {
      return row[props.rowKey] || index;
    };

    const formatValue = (value, column) => {
      if (column.formatter && typeof column.formatter === "function") {
        return column.formatter(value);
      }
      return value;
    };

    // Watchers
    watch(
      () => props.data,
      () => {
        currentPage.value = 1;
      },
    );

    // Watch for prop changes and update internal state
    watch(
      () => props.pageSize,
      (newSize) => {
        internalPageSize.value = newSize;
        currentPage.value = 1;
      },
    );

    return {
      currentPage,
      sortBy,
      sortDirection,
      searchTerm,
      internalPageSize, // Updated name
      hasActions,
      filteredData,
      totalPages,
      paginatedData,
      startIndex,
      endIndex,
      visiblePages,
      sortTable,
      goToPage,
      nextPage,
      previousPage,
      onPageSizeChange,
      onSearch,
      getRowKey,
      formatValue,
    };
  },
};
</script>

<style scoped>
/* Your existing styles remain the same */
.datatable {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.datatable-header {
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.search-box {
  position: relative;
  max-width: 300px;
}

.search-input {
  width: 100%;
  padding: 8px 16px 8px 40px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.table-container {
  overflow-x: auto;
}

.datatable-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.datatable-table th {
  background: #f8f9fa;
  /* Use same horizontal padding as td to ensure column alignment */
  padding: 12px 14px;
  /* Do not force text alignment here; allow column classes (text-left/right/center) to apply */
  font-weight: 600;
  color: #000000;
  border-bottom: 2px solid #e9ecef;
  cursor: default;
  user-select: none;
  vertical-align: middle;
}

.datatable-table th.sortable {
  cursor: pointer;
}

.datatable-table th.sortable:hover {
  background: #e9ecef;
}

.header-content {
  display: flex;
  align-items: center;
  /* Fill the full header cell width so justify-*- classes take effect */
  width: 100%;
  /* Align to start by default; overridden by dynamic classes above */
  justify-content: flex-start;
  gap: 6px;
  font-size: 13px;
  text-align: inherit;
}

.sort-indicator {
  font-size: 12px;
  color: #000000;
}

.datatable-table td {
  padding: 6px 14px;
  border-bottom: 1px solid #e9ecef;
  color: #000000;
  vertical-align: middle;
}

/* Vertical dividers between columns (like classic tables) */
.datatable-table th:not(:last-child),
.datatable-table td:not(:last-child) {
  border-right: 1px solid #e9ecef;
}

.datatable-table tbody tr:hover {
  background: #f8f9fa;
}

.no-data {
  text-align: center;
  color: #000000;
  font-style: italic;
  padding: 16px !important;
}

.actions-cell {
  white-space: nowrap;
  text-align: center;
}

.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 8px;
}

.btn-edit {
  background: #007bff;
  color: white;
}

.btn-edit:hover {
  background: #0056b3;
}

.btn-delete {
  background: #dc3545;
  color: white;
}

.btn-delete:hover {
  background: #c82333;
}

.datatable-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.pagination-info {
  color: #000000;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:not(:disabled):hover {
  background: #e9ecef;
}

.page-number {
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 40px;
  text-align: center;
}

.page-number.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.page-number.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
}

.page-number:not(.ellipsis):hover {
  background: #e9ecef;
}

.page-size-select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  margin-left: 16px;
}
</style>
